import React from 'react';
import { Button } from 'react-bootstrap';
import { FileText } from 'lucide-react';
import { formatCurrency, formatDate } from '../bankFormat';

export const ACTION_TITLES = { register_customer_payment: 'Registrera kundbetalning', register_supplier_payment: 'Registrera leverantörsbetalning', link_existing_payment: 'Koppla registrerad betalning', book_direct_purchase:'Bokför köp', book_from_proposal:'Bokför granskat förslag', link_existing_verification:'Koppla befintligt verifikat', attach_evidence:'Bifoga underlag', settle_payment_rows:'Stäm av betalfil' };
const EFFECT_LABELS = { create_payment: 'Bokförs som betalning', link_payment: 'Kopplas (redan betald)', link_payroll: 'Kopplas till lönekörningen', link_transfer: 'Kopplas till skattekontobokningen', create_transfer: 'Bokförs mot skattekontot', create_manual: 'Bokförs mot motkontot', link_verification: 'Kopplas (redan bokförd)' };

const cell = { padding: '4px 6px' };
const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const money = (ore) => (ore ? formatCurrency(ore / 100) : '');
const fileName = (blobName) => String(blobName || '').split('/').pop();

/**
 * What an action will do, exactly as the backend planned it: the verifikat
 * rows with their sum, or one sentence when nothing is booked. Presentation
 * only; the host puts the buttons in its own footer (ActionPreviewButtons).
 *
 * Drawn like the verifikat preview in Att göra. `flat` drops the tinted card
 * for hosts that are already a form (the bank sheet's booking view).
 */
export default function ActionPreview({ plan, flat = false }) {
    const rows = plan.effects.creates_verification ? plan.effects.journal_rows || [] : [];
    const debit = rows.reduce((sum, row) => sum + (row.debit_ore || 0), 0);
    const credit = rows.reduce((sum, row) => sum + (row.credit_ore || 0), 0);
    return <section
        aria-label="Förhandsgranskning"
        style={flat ? { fontSize: 12 } : { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', fontSize: 12 }}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>
            <FileText size={12} /> <span>{ACTION_TITLES[plan.type]}</span>
        </div>
        <div style={{ color: '#334155', marginBottom: 6 }}>
            <span style={{ marginRight: 12 }}><span style={{ fontWeight: 600 }}>Datum:</span> {formatDate(plan.booking_date)}</span>
            <span><span style={{ fontWeight: 600 }}>Belopp:</span> {formatCurrency(Math.abs(plan.bank.amount_ore) / 100)}</span>
        </div>
        {rows.length > 0 ? <table style={{ width: '100%', borderCollapse: 'collapse', color: '#0f172a' }}>
            <thead>
                <tr style={{ color: '#64748b', textAlign: 'left' }}>
                    <th style={cell}>Konto</th>
                    <th style={{ ...cell, textAlign: 'right' }}>Debet</th>
                    <th style={{ ...cell, textAlign: 'right' }}>Kredit</th>
                </tr>
            </thead>
            <tbody>{rows.map((row, index) => <tr key={index} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={cell}><span style={{ fontWeight: 600 }}>{row.account_number}</span>{row.account_name ? ` ${row.account_name}` : ''}</td>
                <td style={num}>{money(row.debit_ore)}</td>
                <td style={num}>{money(row.credit_ore)}</td>
            </tr>)}</tbody>
            <tfoot>
                <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                    <td style={{ ...cell, textAlign: 'right' }}>Summa</td>
                    <td style={num}>{money(debit)}</td>
                    <td style={num}>{money(credit)}</td>
                </tr>
            </tfoot>
        </table> : <div style={{ color: '#334155' }}>Ingen ny bokföring skapas. {plan.effects.links_bank ? 'Bankhändelsen kopplas.' : 'Bankhändelsens status ändras inte.'}</div>}
        {plan.rows?.length > 0 && <table style={{ width: '100%', borderCollapse: 'collapse', color: '#0f172a', marginTop: 8 }}>
            <thead><tr style={{ color: '#64748b', textAlign: 'left' }}><th style={cell}>Betalning</th><th style={cell}>Effekt</th><th style={{ ...cell, textAlign: 'right' }}>Belopp</th></tr></thead>
            <tbody>{plan.rows.map((row) => <tr key={row.instruction_id} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={cell}>{row.label}</td>
                <td style={cell}>{EFFECT_LABELS[row.effect] || row.effect}</td>
                <td style={num}>{money(row.amount_ore)}</td>
            </tr>)}</tbody>
        </table>}
        {plan.documents?.map(d => <div key={d.document_id} style={{ color: '#334155', marginTop: 6 }}>Underlag: {fileName(d.blob_name)} {d.original_currency && `(${d.original_currency})`}</div>)}
        {plan.payment && <div style={{ color: '#334155', marginTop: 6 }}>Kvar på fakturan: <strong>{formatCurrency(plan.payment.remaining_after_ore / 100)}</strong>{plan.payment.partial ? ' (delbetalning)' : ''}</div>}
    </section>;
}

/** The preview's two decisions, for the host's footer. */
export function ActionPreviewButtons({ busy, unknown, onExecute, onEdit }) {
    return <>
        <Button variant="outline-secondary" size="sm" disabled={busy || unknown} onClick={onEdit}>Ändra val</Button>
        <Button size="sm" disabled={busy} onClick={onExecute}>{unknown ? 'Kontrollera / försök igen' : busy ? 'Bearbetar…' : 'Bekräfta'}</Button>
    </>;
}
